import { useEffect, useState } from "react";
import type { VideoRTC } from "@client/vendor/go2rtc/video-rtc";
import {
  jitterDelayMs,
  pickInboundVideo,
  resolveTransport,
  type InboundVideoReport,
  type Transport,
} from "@client/lib/foscam/streamStats";

/** Jak často se statistiky čtou. Sekunda stačí — je to informativní chip, ne graf. */
const SAMPLE_MS = 1_000;

export interface StreamStats {
  transport: Transport;
  /** Průměrné zdržení jitter bufferu v ms, `null` dokud není co měřit. */
  latencyMs: number | null;
  fps: number | null;
}

const EMPTY: StreamStats = { transport: "unknown", latencyMs: null, fps: null };

/**
 * Skutečné parametry běžícího streamu z `RTCPeerConnection.getStats()`.
 *
 * Nahrazuje původní natvrdo zadaných 200 ms v `CameraTile`. Chip nad obrazem tak
 * ukazuje pravdu včetně toho, když kamera spadne z WebRTC na pomalejší MSE nebo HLS —
 * což dosud nebylo poznat vůbec.
 *
 * Vzorkuje se v intervalu, ne při renderu: `getStats()` je async a nečisté čtení,
 * které do renderu nepatří. Interval běží jen když `enabled`, tedy jen u kamery
 * s viditelným ovládáním — v mřížce netiká nic.
 *
 * @param getEngine getter na custom element go2rtc (drží `pc` i `mseCodecs`)
 */
export function useWebrtcStats(getEngine: () => VideoRTC | null, enabled = true): StreamStats {
  const [stats, setStats] = useState<StreamStats>(EMPTY);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;

    const sample = async () => {
      const el = getEngine();
      if (!el) return;
      const transport = resolveTransport(el.pc?.connectionState, el.mseCodecs, !!el.video);

      let latencyMs: number | null = null;
      let fps: number | null = null;
      if (el.pc) {
        try {
          const report = await el.pc.getStats();
          const inbound = pickInboundVideo(report as unknown as Iterable<InboundVideoReport>);
          latencyMs = jitterDelayMs(inbound);
          fps = typeof inbound?.framesPerSecond === "number" ? inbound.framesPerSecond : null;
        } catch {
          /* spojení se mezitím zavřelo — necháme null */
        }
      }
      if (!alive) return;
      setStats((prev) =>
        prev.transport === transport && prev.latencyMs === latencyMs && prev.fps === fps
          ? prev
          : { transport, latencyMs, fps }
      );
    };

    void sample();
    const id = setInterval(() => void sample(), SAMPLE_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled, getEngine]);

  return stats;
}
